import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Trash2, Plus, Minus, LayoutGrid } from 'lucide-react';

const uid = () => Math.random().toString(36).substring(2, 10);

/* S1000D Color codes */
const COLOR_OPTIONS = [
  { value: 'co01', label: 'Green', css: '#16a34a' },
  { value: 'co02', label: 'Amber', css: '#d97706' },
  { value: 'co03', label: 'Yellow', css: '#eab308' },
  { value: 'co04', label: 'Red', css: '#dc2626' },
  { value: 'co05', label: 'Blue', css: '#2563eb' },
  { value: 'co06', label: 'Magenta', css: '#c026d3' },
  { value: 'co07', label: 'Cyan', css: '#0891b2' },
  { value: 'co08', label: 'Grey', css: '#6b7280' },
  { value: 'co09', label: 'White', css: '#f8fafc' },
  { value: 'co10', label: 'Black', css: '#1e293b' },
  { value: 'co51', label: 'Default', css: '#94a3b8' },
];

const colorCss = (code) => COLOR_OPTIONS.find(c => c.value === code)?.css || '#94a3b8';

/* ═══════════════════════════════════════════
   CAPTION GROUP EDITOR
   S1000D captionGroup with colored captionEntries
   ═══════════════════════════════════════════ */
export const CaptionGroupEditor = ({ element, onUpdate, onDelete, compact = false }) => {
  const cols = element.cols || 2;
  const colspecs = element.colspecs || Array.from({ length: cols }, (_, i) => ({
    colnum: String(i + 1), colname: `col${i + 1}`, colwidth: '30mm',
  }));
  const rows = element.rows || [Array(cols).fill(null).map((_, i) => ({
    colname: `col${i + 1}`, color: 'co51', captionWidth: '30mm', text: '',
  }))];

  const emit = (updates) => onUpdate({ ...element, ...updates });

  // Column operations
  const addCol = () => {
    const newCols = cols + 1;
    const newColspecs = [...colspecs, { colnum: String(newCols), colname: `col${newCols}`, colwidth: '30mm' }];
    const newRows = rows.map(row => [...row, { colname: `col${newCols}`, color: 'co51', captionWidth: '30mm', text: '' }]);
    emit({ cols: newCols, colspecs: newColspecs, rows: newRows });
  };

  const removeCol = () => {
    if (cols <= 1) return;
    const newCols = cols - 1;
    emit({
      cols: newCols,
      colspecs: colspecs.slice(0, newCols),
      rows: rows.map(row => row.slice(0, newCols)),
    });
  };

  // Row operations
  const addRow = () => {
    const newRow = Array(cols).fill(null).map((_, i) => ({
      colname: colspecs[i]?.colname || `col${i + 1}`, color: 'co51', captionWidth: '30mm', text: '',
    }));
    emit({ rows: [...rows, newRow] });
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    emit({ rows: rows.filter((_, i) => i !== idx) });
  };

  // Cell editing
  const updateCell = (ri, ci, updates) => {
    const newRows = rows.map((row, r) =>
      r === ri ? row.map((cell, c) => c === ci ? { ...cell, ...updates } : cell) : [...row]
    );
    emit({ rows: newRows });
  };

  const updateColWidth = (ci, val) => {
    const newSpecs = colspecs.map((cs, i) => i === ci ? { ...cs, colwidth: val } : cs);
    emit({ colspecs: newSpecs });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white group" data-testid={`caption-group-${element.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-t-lg border-b border-emerald-200">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-4 h-4 text-emerald-600" />
          <Badge className="text-[10px] bg-emerald-100 text-emerald-700">captionGroup</Badge>
          <span className="text-xs text-slate-400">{cols} cols × {rows.length} rows</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={addCol} className="p-1 text-slate-400 hover:text-emerald-600 rounded hover:bg-emerald-50" title="Add column">
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button onClick={removeCol} className="p-1 text-slate-400 hover:text-red-500 rounded hover:bg-red-50" title="Remove last column" disabled={cols <= 1}>
            <Minus className="w-3.5 h-3.5" />
          </button>
          {onDelete && (
            <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500 ml-1" data-testid={`delete-caption-${element.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* applicRefId */}
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            <Label className="text-[10px] text-slate-400">applicRefId (optional)</Label>
            <Input
              value={element.applicRefId || ''}
              onChange={e => emit({ applicRefId: e.target.value })}
              placeholder="e.g. app-0001"
              className="text-xs h-7 font-mono"
              data-testid={`caption-appref-${element.id}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-slate-400">Col widths</Label>
            <div className="flex gap-1">
              {colspecs.map((cs, ci) => (
                <input key={ci} value={cs.colwidth}
                  onChange={e => updateColWidth(ci, e.target.value)}
                  className="text-[10px] px-1.5 py-0.5 border border-slate-200 rounded w-14 text-center font-mono text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Caption grid */}
        <div className="overflow-x-auto">
          <div className="space-y-1.5" data-testid={`caption-grid-${element.id}`}>
            {rows.map((row, ri) => (
              <div key={ri} className="flex items-stretch gap-1.5 group/row">
                {row.map((cell, ci) => (
                  <div key={ci} className="flex-1 border border-slate-200 rounded-lg overflow-hidden" style={{ borderLeftColor: colorCss(cell.color), borderLeftWidth: '3px' }}>
                    <div className="flex items-center gap-1 px-1.5 py-1 bg-slate-50 border-b border-slate-100">
                      <Select value={cell.color || 'co51'} onValueChange={v => updateCell(ri, ci, { color: v })}>
                        <SelectTrigger className="h-5 text-[9px] w-[90px] bg-white px-1" data-testid={`caption-color-${ri}-${ci}-${element.id}`}>
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorCss(cell.color) }} />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {COLOR_OPTIONS.map(co => (
                            <SelectItem key={co.value} value={co.value}>
                              <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: co.css }} />
                                <span className="text-[10px]">{co.label}</span>
                                <span className="text-[9px] text-slate-400 font-mono">{co.value}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input value={cell.captionWidth || ''} onChange={e => updateCell(ri, ci, { captionWidth: e.target.value })}
                        className="text-[9px] px-1 py-0.5 border border-slate-200 rounded w-12 text-center font-mono text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                        title="captionWidth"
                      />
                    </div>
                    <input
                      value={cell.text || ''}
                      onChange={e => updateCell(ri, ci, { text: e.target.value })}
                      className={`w-full px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-300 bg-white ${compact ? 'text-xs py-1' : ''}`}
                      placeholder="Caption text..."
                      data-testid={`caption-text-${ri}-${ci}-${element.id}`}
                    />
                  </div>
                ))}
                <div className="flex items-center">
                  <button onClick={() => removeRow(ri)} className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover/row:opacity-100 transition-opacity" title="Delete row">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add row */}
        <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-emerald-600" onClick={addRow} data-testid={`add-caption-row-${element.id}`}>
          <Plus className="w-3 h-3" /> Add Row
        </Button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PARSE captionGroup from DOM element
   ═══════════════════════════════════════════ */
export function parseCaptionGroup(cgEl) {
  const colsAttr = parseInt(cgEl.getAttribute('cols') || '2', 10);
  const applicRefId = cgEl.getAttribute('applicRefId') || '';

  // Parse colspecs
  const colspecs = [];
  cgEl.querySelectorAll(':scope > colspec').forEach(cs => {
    colspecs.push({
      colnum: cs.getAttribute('colnum') || String(colspecs.length + 1),
      colname: cs.getAttribute('colname') || `col${colspecs.length + 1}`,
      colwidth: cs.getAttribute('colwidth') || '30mm',
    });
  });
  while (colspecs.length < colsAttr) {
    colspecs.push({ colnum: String(colspecs.length + 1), colname: `col${colspecs.length + 1}`, colwidth: '30mm' });
  }

  // Parse captionBody rows
  const rows = [];
  const body = cgEl.querySelector(':scope > captionBody');
  if (body) {
    body.querySelectorAll(':scope > captionRow').forEach(rowEl => {
      const rowCells = [];
      rowEl.querySelectorAll(':scope > captionEntry').forEach((entry, ci) => {
        const colname = entry.getAttribute('colname') || colspecs[ci]?.colname || `col${ci + 1}`;
        const captionEl = entry.querySelector(':scope > caption');
        const color = captionEl?.getAttribute('color') || 'co51';
        const captionWidth = captionEl?.getAttribute('captionWidth') || '30mm';
        const captionLine = captionEl?.querySelector(':scope > captionLine');
        const text = captionLine?.textContent?.trim() || '';
        rowCells.push({ colname, color, captionWidth, text });
      });
      // Pad to col count
      while (rowCells.length < colsAttr) {
        rowCells.push({ colname: colspecs[rowCells.length]?.colname || `col${rowCells.length + 1}`, color: 'co51', captionWidth: '30mm', text: '' });
      }
      rows.push(rowCells);
    });
  }
  if (rows.length === 0) {
    rows.push(Array(colsAttr).fill(null).map((_, i) => ({
      colname: colspecs[i]?.colname || `col${i + 1}`, color: 'co51', captionWidth: '30mm', text: '',
    })));
  }

  return {
    id: cgEl.getAttribute('id') || uid(),
    type: 'captionGroup',
    cols: colsAttr,
    applicRefId,
    colspecs,
    rows,
  };
}
